import { FaultType } from '../../models/assistance.model';
import { UnregisteredSteps } from '../../containers/public/unregistered/unregistered-types';
import { DUMMY_IDENTIFIER } from '../../constants';
import { trackEvent } from '../../appInsights';
import AdacaAPI from '..';

export type RequestCraneBody = {
  identifier: string;
  faultType: FaultType;
  latitude: number;
  longitude: number;
};

export type Crane = {
  codigoGrua: string;
  codigoOperario: string;
  nombreOperario: string;
  codigoColaborador: string;
  latitud: number;
  longitud: number;
};

export interface IRequestLocationResponse {
  codigo: string;
  descripcion: string;
  listadoGruas: Crane[] | null;
}

export type CraneLocationParams = {
  provider?: string;
  intervention?: string;
  file?: string;
  identifier?: string;
};

export interface IGetCraneLocationResponse {
  identificador: string;
  intervencion: string;
  expediente: string;
  colaborador: string;
  estado: string;
  latitud: string;
  longitud: string;
  recargarPagina: number;
  medio?: {
    medioAsignado: boolean;
    tipoMedio: string;
    colaborador: string;
    latitud: number;
    longitud: number;
    intervencion: string;
  };
  tareaMedio?: boolean;
  llegadaGrua?: number;
}

export interface IGetInterventionResponse {
  entidad: string;
  numeroPoliza: string;
  numeroExpediente: string;
  intervencion: string;
  matricula: string;
  personaContacto: string;
  telefonoContacto: string;
  nombreProveedor: string;
  codigoProveedor: string;
  localidadOrigenSiniestro: string;
  provinciaOrigenSiniestro: string;
  tiempoEstimado: number;
  tiempoColaborador: number;
  marca: string;
  modelo: string;
  color: string;
  esdeMovilidad: boolean;
  estadoExpediente: string;
  estadoIntervencion: string;
  estadoEnlace: number;
  estadoAsistencia?: number;
  contactoCompania: string;
  latitud: number;
  longitud: number;
  fechaDiferido?: string;
  tareaMedio?: boolean;
  medio?: {
    medioAsignado: boolean;
    tipoMedio: string;
    colaborador: string;
    latitud: number;
    longitud: number;
    intervencion: string;
  };
  tipoEnvio: number;
}

export interface IGetFilePhoneResponse {
  telefono: string;
}

export interface IAssistanceItem {
  identificador: string;
  activa: boolean;
  codigoEntidad: string;
  entidad?: string;
  fechaAlta?: string;
  poliza?: string;
  expediente?: string;
  colaborador?: string;
  anulada?: string;
  fechaAnulada?: string;
  fechaFinServicio?: string;
  tiempoEstimado?: string;
  fechaDiferido?: string;
  fechaLocalizadoVehiculo?: string;
  colaboradorAutomatico?: boolean;
  intervencion?: string;
  diferido?: boolean;
  cif?: string;
  matricula?: string;
}

// (Y) Interfaz de la respuesta de la llamada a la API de datos iniciales de la asistencia
export interface IAssistanceInformation {
  codigo: string;
  descripcion: string;
  expediente: string;
  colaborador: string;
  intervencion: string;
  tiempoEstimado: string;
  // Nuevos campos
  asistencia?: UnregisteredSteps;
  estadoEnlace: number;
  estadoAsistencia?: number;
  media?: boolean;
}

export interface IExistReply {
  codigo: string;
  descripcion: string;
}

const requestCrane = async (body?: RequestCraneBody) => {
  if (!body) return;
  const { data } = await AdacaAPI.post<IRequestLocationResponse>(`/assistance/${body.identifier}/request-crane`, body);

  return data;
};

const requestACall = async (intervention?: string) => {
  if (!intervention) return;
  await AdacaAPI.post(`/Assistance/${intervention}/call-me-back`);
};

const requestAnInsuranceCall = async (insuranceCode?: string) => {
  const param = insuranceCode ? `/${insuranceCode}` : '';
  await AdacaAPI.get(`/InsuranceCompanies/call-me-back${param}`);
};

const requestACallByPhone = async (phoneNumber: string, intervention: string, provider?: string) => {
  const body = { intervencion: intervention, telefono: phoneNumber, entidad: provider };
  await AdacaAPI.post(`/Assistance/call-me-back-phone`, body);
};

const getCraneLocation = async (params?: CraneLocationParams) => {
  if (!params || !params.identifier) return;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { identifier, ...paramsFiltered } = params;
  const { data } = await AdacaAPI.get<IGetCraneLocationResponse>(`/assistance/crane-location`, {
    params: paramsFiltered,
  });
  return data;
};

const getIntervention = async (intervention?: string) => {
  if (!intervention) return;
  const { data } = await AdacaAPI.get<IGetInterventionResponse>(`/Assistance/AIA/${intervention}`);
  return data;
};

const getFilePhone = async (identifier?: string) => {
  if (!identifier) return;
  const { data } = await AdacaAPI.get<IGetFilePhoneResponse>(`/Assistance/${identifier}/phone`);
  return data;
};

const getAssistances = async () => {
  const { data } = await AdacaAPI.get<IAssistanceItem[]>(`/Assistance`);
  return data;
};

// (Y) Llamada a la API de información inicial de la asistencia por URL (SMS)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getAssistanceInformation = async (identifier?: string, insuranceCompany?: string, caller?: string) => {
  if (!identifier || !insuranceCompany || identifier === DUMMY_IDENTIFIER) return null;

  try {
    const { data } = await AdacaAPI.get<IAssistanceInformation>(`/Assistance/${identifier}`, {
      params: { insuranceCompany },
    });

    trackEvent('verificacion-poliza', { identifier, insuranceCompany, data });

    return data;
  } catch (error: any) {
    // Capturar datos del cuerpo de la respuesta incluso cuando hay error 404
    if (error?.response?.status === 404 && error?.response?.data) {
      trackEvent('verificacion-poliza', { identifier, insuranceCompany, data: error.response.data, status: 404 });

      return error.response.data;
    }

    // Si es otro tipo de error, relanzar la excepción
    throw error;
  }
};

const craneArrivedMutation = async (identifier: string, file: string, intervencion: string, didArrive: boolean) => {
  if (!identifier) return;
  await AdacaAPI.post(`/Assistance/${identifier}/crane-arrived`, {
    identifier,
    file,
    intervencion,
    didArrive,
    latitude: 0,
    longitude: 0,
  });
};

const existInsuredReply = async (intervencion: string) => {
  if (!intervencion) return;
  const { data } = await AdacaAPI.get<IExistReply>(`/Assistance/${intervencion}/exists-insured-reply`);
  return data;
};

export interface IRecaptchaToken {
  token: string;
  expectedAction: string;
}

const verifyRecaptcha = async (body?: IRecaptchaToken): Promise<any> => {
  if (!body) return;
  const response = await AdacaAPI.post<boolean>(`/Assistance/verificaToken`, body);

  return response.status === 202 || response.status === 200;
};

export {
  requestCrane,
  getCraneLocation,
  getIntervention,
  requestACall,
  requestACallByPhone,
  getAssistances,
  getAssistanceInformation,
  craneArrivedMutation,
  requestAnInsuranceCall,
  existInsuredReply,
  verifyRecaptcha,
  getFilePhone,
};
